import AsyncStorage from '@react-native-async-storage/async-storage';
import { type ActionArgs, assertEvent, assign } from 'xstate5';

import { mergeKnownProperties } from '../helpers';
import { DIETARY_PREFERENCES_KEY } from './constants';
import { type Context, generateInitialContext } from './context';
import { type Event } from './event';
import { type DietaryPreferencesTypes } from './types';

// ─────────────────────────────────────────────────────────────────────────────

const togglePreference: Action = assign((args) => {
  const { context, event } = args;

  assertEvent(event, 'TOGGLE_PREFERENCE');

  const { pendingPreferences } = context;
  const maybePendingPreference = pendingPreferences[event.value] ?? false;

  return {
    pendingPreferences: {
      ...pendingPreferences,
      [event.value]: !maybePendingPreference,
    },
  };
});

const resetPendingChanges: Action = assign((args) => {
  const { preferences } = args.context;

  return {
    pendingPreferences: { ...preferences },
  };
});

const confirmPendingChanges: Action = assign((args) => {
  const { pendingPreferences } = args.context;

  return {
    preferences: { ...pendingPreferences },
  };
});

const resetPreferences: Action = assign(generateInitialContext());

const clearHydratedDietaryPreferences: Action = () => {
  void AsyncStorage.removeItem(DIETARY_PREFERENCES_KEY);
};

const processRehydratedPreferences: Action<Record<string, unknown>> = assign(
  (args, rehydratedPreferences) => {
    const { context } = args;

    const updatedPreferences = mergeKnownProperties<
      DietaryPreferencesTypes,
      Partial<DietaryPreferencesTypes>
    >(context.preferences, rehydratedPreferences);

    return {
      preferences: updatedPreferences,
      pendingPreferences: updatedPreferences,
    };
  },
);

const syncPreferences: Action<{
  preferences: Partial<DietaryPreferencesTypes>;
}> = assign((args, params) => {
  const { context } = args;
  const { preferences: updatedPreferences } = params;

  const { preferences } = context;

  return {
    preferences: { ...preferences, ...updatedPreferences },
    pendingPreferences: { ...preferences, ...updatedPreferences },
  };
});

const setActivateAfterFetch: Action = assign({ activateAfterFetch: true });

const resetActivateAfterFetch: Action = assign({ activateAfterFetch: false });

const setStayActiveAfterConfirm: Action = assign(({ event }) => {
  assertEvent(event, 'START');

  return { stayActiveAfterConfirm: event.stayActiveAfterConfirm ?? false };
});

const resetStayActiveAfterConfirm: Action = assign({
  stayActiveAfterConfirm: false,
});

// ─────────────────────────────────────────────────────────────────────────────

export const Actions = {
  togglePreference,
  resetPendingChanges,
  confirmPendingChanges,
  resetPreferences,
  clearHydratedDietaryPreferences,
  processRehydratedPreferences,
  syncPreferences,
  setActivateAfterFetch,
  resetActivateAfterFetch,
  setStayActiveAfterConfirm,
  resetStayActiveAfterConfirm,
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Action<ActionParams extends Record<string, unknown> | void = void> = (
  actionArgs: ActionArgs<Context, Event, Event>,
  params: ActionParams,
) => void;
